import React, { CSSProperties, FC, ReactNode, useEffect, useState } from "react";
import ShareButton from "./components/ShareButton/ShareButton";
import DownloadButton from "./components/DownloadButton/DownloadButton";
import { openSnapchat } from "./utils/redirect";
import { CameraKitContext } from "./contexts/CameraKitContext";
import { CameraSourceContext } from "./contexts/CameraSourceContext";
import Player from "./components/Player/Player";
import { useCameraSource } from "./hooks/useCameraSource";

// Set apiToken with an API token for the desired app. (https://kit.snapchat.com/manage)
const apiToken = process.env.REACT_APP_CK_API_TOKEN
const defaultLensGroup = process.env.REACT_APP_CK_LENS_GROUP_ID; // Camera kit lens group
const title = process.env.TITLE; // Camera kit lens group
let calledOpenSnapchat = false;

const TapButtonStyle: CSSProperties =
{
    margin: "0 3vh 3vh 3vh",
    fontFamily: "Avenir Next,Helvetica,sans-serif",
    fontWeight: 500,
    color: "black",
    fontSize: 16,
    lineHeight: "16px",
    background: "none",
    padding: "10px 20px",
    border: "1px solid black",
    borderRadius: 100
}

const CentralDivStyle: CSSProperties =
{
    display: "flex",
    position: "absolute",
    top: "45%",
    flexDirection: "column",
    alignItems: "center"
}

const ErrorMessageStyle: CSSProperties =
{
    margin: "1em",
}

interface WebArPlayerProps {
  lensId: number;
  lensPublicUrl: string;
  lensDeepLink: string;
}

interface WebArPlayerContentProps {
  onCameraPermissionError: (hasErrored: boolean) => void;
}

const WebArPlayerContent: React.FC<WebArPlayerContentProps> = ({ onCameraPermissionError }) => {
  const { hasVideoFeed } = useCameraSource();

  // Hide error message when video feed is working
  useEffect(() => {
    if (hasVideoFeed) {
      onCameraPermissionError(false);
    }
  }, [hasVideoFeed, onCameraPermissionError]);

  return (
    <>
      <Player />
    </>
  );
};

export const WebArPlayer: React.FC<WebArPlayerProps> = ({
  lensId,
  lensPublicUrl,
  lensDeepLink,
}) => {

  const isMobile  = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const [isMotionPermissionGranted, setIsMotionPermissionGranted] = useState(true);
  const [isCameraPermissionError, setIsCameraPermissionError] = useState(false);

  useEffect(() => {
    if (!calledOpenSnapchat) {
      if (isAndroid) {
        requestCameraPermission();
        setTimeout(() => openSnapchat(lensPublicUrl, lensDeepLink), 1500);
      } else {
        openSnapchat(lensPublicUrl, lensDeepLink);
      }
      calledOpenSnapchat = true;
    }
  }, [lensPublicUrl, lensDeepLink]);

  // Request permission for DeviceMotion and DeviceOrientation
  function requestMotionPermission() {
    const isMotionAndOrientationBrowser =
          typeof DeviceMotionEvent !== "undefined" &&
          typeof DeviceOrientationEvent !== "undefined" &&
          typeof (DeviceMotionEvent as any).requestPermission === "function" &&
          typeof (DeviceOrientationEvent as any).requestPermission === "function";
    if (isMotionAndOrientationBrowser) {
      Promise.all([
        (DeviceMotionEvent as any).requestPermission(),
        (DeviceMotionEvent as any).requestPermission()
      ])
      .then(([motionPermission, orientationPermission]) => {
        if (motionPermission === 'granted' && orientationPermission === 'granted') {
          setIsMotionPermissionGranted(true);
        }
      })
      .catch((error) => console.log(error));
    } else {
      // Permission is not required
      setIsMotionPermissionGranted(true);
    }
  }

  function requestCameraPermission() {
    navigator.mediaDevices.getUserMedia({ video: true })
    .then(() => {
      console.log("Camera permission granted");
    })
    .catch((error) => console.log(error));
  }

  const handleCameraPermissionError = (hasErrored: boolean) => {
    setIsCameraPermissionError(hasErrored);
  };

  return (
    <div>
      { isMobile &&
          <div className="container">
            <ShareButton pageTitle={title!}/>
            <div className="canvas-container">
              <div className="footer">
                {isCameraPermissionError && <div style={ErrorMessageStyle}>Not working on your device?</div>}
                <DownloadButton/>
                <img className="logo" src={require("./icons/logo.png")} />
              </div>
              { isMotionPermissionGranted ?
                <>
                  <CameraKitContext
                    apiToken={apiToken!}
                    lensGroupId={defaultLensGroup!}
                    lensId={lensId}
                    shouldUseWorker={true}
                  >
                    <CameraSourceContext
                      initialDeviceId={undefined}
                      fpsLimit={undefined}
                      onCameraPermissionError={handleCameraPermissionError}
                    >
                      <WebArPlayerContent onCameraPermissionError={handleCameraPermissionError} />
                    </CameraSourceContext>
                  </CameraKitContext>
                </>
               :
                <div style={CentralDivStyle}>
                  <button onClick={requestMotionPermission} style={TapButtonStyle}>Tap to start</button>
                </div>
              }
            </div>
          </div>
      }
      { !isMobile &&
        <div className="non-mobile-message">
          Please open the Web AR player on a mobile device.
        </div>
      }
    </div>
  );
};

export default WebArPlayer;

